import type { PlanIntent } from "@/client/stores/payment";

/*
 * The plan (and code) someone picked on firstround.com.au, carried through sign-up so the plan
 * dialog opens with it. It rides in the sign-up link (?redirect=/dashboard/practice-interview?
 * plan=starter&code=…), and the sign-in / sign-up pages also keep it here, in localStorage: email
 * verification can finish in another tab and Google sign-in can lose the address, and either way
 * the dashboard still opens the dialog with their plan.
 */

const PLAN_KEYS = new Set(["free", "starter", "active"]);
const STORAGE_KEY = "first-round-plan-intent";
const MAX_AGE_MS = 60 * 60 * 1000;

/** ?plan=starter&code=MICHAEL (from firstround.com.au, through sign-up) or ?plans=open. */
export const readPlanIntent = (params: URLSearchParams): PlanIntent | null => {
  const plan = params.get("plan");
  const code = params.get("code")?.trim();
  if (!plan && !code && params.get("plans") === null) return null;
  return {
    plan: plan && PLAN_KEYS.has(plan) ? (plan as PlanIntent["plan"]) : undefined,
    code: code ? code.slice(0, 64) : undefined,
  };
};

/** The plan or code in a path's query ("/dashboard/practice-interview?plan=starter"), if any. */
export const planIntentInPath = (path: string): PlanIntent | null => {
  const query = path.includes("?") ? path.slice(path.indexOf("?") + 1) : "";
  const intent = readPlanIntent(new URLSearchParams(query));
  return intent?.plan || intent?.code ? intent : null;
};

export const rememberPlanIntent = (intent: PlanIntent) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...intent, at: Date.now() }));
  } catch {
    // Storage blocked: the sign-up link still carries it.
  }
};

export const forgetPlanIntent = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
};

/** The remembered plan, once: reading it forgets it. Older than an hour counts as abandoned. */
export const takeRememberedPlanIntent = (): PlanIntent | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    localStorage.removeItem(STORAGE_KEY);
    const stored = JSON.parse(raw) as PlanIntent & { at?: number };
    if (!stored.at || Date.now() - stored.at > MAX_AGE_MS) return null;
    const intent = readPlanIntent(
      new URLSearchParams({
        ...(stored.plan ? { plan: stored.plan } : {}),
        ...(stored.code ? { code: stored.code } : {}),
      }),
    );
    return intent?.plan || intent?.code ? intent : null;
  } catch {
    return null;
  }
};
